import React, { useState, useMemo } from 'react';
import { UserPlus } from 'lucide-react';
import { memberService, type Member, type Role } from '../../services/memberService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ConfirmationDialog } from '../../components/common/ConfirmationDialog';
import { useToast } from '../../components/common/Toast';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../store/authStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PasswordModal } from './components/PasswordModal';
import { MemberFilters } from './components/MemberFilters';
import { MemberInviteForm } from './components/MemberInviteForm';
import { MemberListItem } from './components/MemberListItem';
import { EditMemberRoleModal } from './components/EditMemberRoleModal';
import { InviteErrorModal, type InviteError } from './components/InviteErrorModal';

export const MemberSettings: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const currentUser = useAuthStore((state) => state.user);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: membersRaw = [], isLoading } = useQuery<Member[]>({
    queryKey: ['members'],
    queryFn: memberService.getMembers,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: rolesRaw = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: memberService.getRoles,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const currentUserRole = currentUser?.role ?? '';
  const isCurrentUserSuperAdmin = currentUserRole.toLowerCase() === 'super_admin';

  const assignableRoles = useMemo(() => {
    return rolesRaw.filter((r) => {
      // Non-super-admin users cannot assign super_admin.
      if (!isCurrentUserSuperAdmin && r.name === 'super_admin') return false;
      return true;
    });
  }, [rolesRaw, isCurrentUserSuperAdmin]);

  // Flat name list for backwards-compat with filter dropdowns and inviteRole sync
  const roles = useMemo(() => assignableRoles.map((r) => r.name), [assignableRoles]);

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  // Sync inviteRole if current selection becomes invalid
  if (roles.length > 0 && !roles.includes(inviteRole)) {
    setInviteRole(roles[0]);
  }

  const { can } = usePermissions();
  const canWrite = can('members', 'write');
  const canDelete = can('members', 'delete');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{ email: string; password?: string; title: string }>({
    email: '',
    title: '',
  });
  const [confirmResetMember, setConfirmResetMember] = useState<Member | null>(null);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<Member | null>(null);
  const [confirmReactivateMember, setConfirmReactivateMember] = useState<Member | null>(null);
  const [inviteError, setInviteError] = useState<InviteError | null>(null);
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [editRoleMember, setEditRoleMember] = useState<Member | null>(null);
  const [editRoleValue, setEditRoleValue] = useState<string>('');
  const [editRoleError, setEditRoleError] = useState<string | null>(null);
  const [showAdminInviteWarning, setShowAdminInviteWarning] = useState(false);
  const [showAdminUpdateWarning, setShowAdminUpdateWarning] = useState(false);

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteName('');
    setInviteRole('viewer');
    setInviteEmailError(null);
    setInviteError(null);
    setShowAdminInviteWarning(false);
  };

  const members = useMemo(() => {
    const sorted = [...membersRaw].sort((a, b) => {
      if (a.id === currentUser?.id) return -1;
      if (b.id === currentUser?.id) return 1;
      return 0;
    });

    return sorted.filter((member) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        !search ||
        member.full_name?.toLowerCase().includes(search) ||
        member.email.toLowerCase().includes(search);

      const matchesRole = roleFilter === 'all' || member.role === roleFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'inactive'
          ? !member.is_active
          : statusFilter === 'active'
            ? member.is_active && member.status === 'active'
            : member.status === statusFilter);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [membersRaw, currentUser?.id, searchTerm, roleFilter, statusFilter]);

  const isDuplicateEmailError = (error: unknown): boolean => {
    const err = error as { response?: { status?: number; data?: { error?: string } } };
    const status = err?.response?.status;
    const message = err?.response?.data?.error?.toLowerCase() ?? '';
    return (
      status === 409 ||
      message.includes('23505') ||
      message.includes('duplicate key') ||
      message.includes('users_company_id_email_key') ||
      message.includes('already exists')
    );
  };

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = inviteName.trim();
    const trimmedEmail = inviteEmail.trim().toLowerCase();
    if (!trimmedEmail || !trimmedName) return;

    setInviteEmailError(null);

    // Client-side precheck: if email matches an existing member in this company,
    // surface a helpful message (with reactivation option for inactive members)
    // instead of letting the server return a generic unique-constraint error.
    const existing = members.find((m: Member) => m.email.toLowerCase() === trimmedEmail);
    if (existing) {
      setInviteError({
        title: t('settingsMember.errors.duplicateTitle'),
        message: existing.is_active
          ? t('settingsMember.errors.duplicateActive')
          : t('settingsMember.errors.duplicateInactive'),
        existingMember: existing,
      });
      setInviteEmailError(t('settingsMember.errors.emailInUse'));
      return;
    }

    // Admin role is permanent and cannot be changed later — confirm before sending.
    if (inviteRole.toLowerCase() === 'admin' && !showAdminInviteWarning) {
      setShowAdminInviteWarning(true);
      return;
    }

    void submitInvite();
  };

  const submitInvite = async () => {
    const trimmedName = inviteName.trim();
    const trimmedEmail = inviteEmail.trim().toLowerCase();
    try {
      const response = await memberService.inviteMember({
        email: trimmedEmail,
        full_name: trimmedName,
        role: inviteRole,
      });
      await queryClient.invalidateQueries({ queryKey: ['members'] });
      resetInviteForm();
      setIsInviteOpen(false);

      // Show credential modal
      setModalData({
        title: t('settingsMember.toast.memberAddedTitle'),
        email: response.user.email,
        password: response.password,
      });
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to invite member:', error);

      if (isDuplicateEmailError(error)) {
        // Reload members so we can offer reactivation if the duplicate exists
        // but wasn't in our local list yet (e.g. soft-deleted / race condition).
        await queryClient.invalidateQueries({ queryKey: ['members'] });
        const latest = membersRaw.find((m: Member) => m.email.toLowerCase() === trimmedEmail);
        setInviteError({
          title: t('settingsMember.errors.duplicateTitle'),
          message:
            latest && !latest.is_active
              ? t('settingsMember.errors.duplicateInactive')
              : t('settingsMember.errors.duplicateActive'),
          existingMember: latest,
        });
        setInviteEmailError(t('settingsMember.errors.emailInUse'));
        return;
      }

      setInviteError({
        title: t('settingsMember.errors.sendInviteTitle', undefined, 'Failed to invite member'),
        message: t('settingsMember.errors.sendInvite'),
      });
    }
  };

  const confirmReset = async () => {
    if (!confirmResetMember) return;
    try {
      const response = await memberService.resetPassword(confirmResetMember.id);
      setModalData({
        title: t('settingsMember.toast.passwordResetTitle'),
        email: confirmResetMember.email,
        password: response.password,
      });
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert(t('settingsMember.errors.resetPassword'));
    } finally {
      setConfirmResetMember(null);
    }
  };

  const confirmRemove = async () => {
    if (!confirmRemoveMember) return;
    try {
      await memberService.removeMember(confirmRemoveMember.id);
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (error) {
      console.error('Failed to deactivate member:', error);
      alert(t('settingsMember.errors.removeMember'));
    } finally {
      setConfirmRemoveMember(null);
    }
  };

  const handleEditRole = (member: Member) => {
    setEditRoleError(null);
    setEditRoleValue(member.role ?? assignableRoles[0]?.name ?? '');
    setEditRoleMember(member);
  };

  const confirmRoleUpdate = () => {
    if (!editRoleMember || !editRoleValue) return;
    // Admin role is permanent — confirm before promoting.
    if (editRoleValue.toLowerCase() === 'admin' && !showAdminUpdateWarning) {
      setShowAdminUpdateWarning(true);
      return;
    }
    void submitRoleUpdate();
  };

  const submitRoleUpdate = async () => {
    if (!editRoleMember || !editRoleValue) return;
    setEditRoleError(null);
    try {
      await memberService.updateRole(editRoleMember.id, editRoleValue);
      await queryClient.invalidateQueries({ queryKey: ['members'] });
      setShowAdminUpdateWarning(false);
      setEditRoleMember(null);
      showToast(t('settingsMember.toast.roleUpdatedMessage', { email: editRoleMember.email }), 'success');
    } catch (error) {
      console.error('Failed to update role:', error);
      const err = error as { response?: { data?: { error?: string } } };
      setShowAdminUpdateWarning(false);
      setEditRoleError(err?.response?.data?.error ?? t('settingsMember.errors.updateRole'));
    }
  };

  const confirmReactivate = async () => {
    if (!confirmReactivateMember) return;
    try {
      await memberService.reactivateMember(confirmReactivateMember.id);
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (error) {
      console.error('Failed to reactivate member:', error);
      alert(t('settingsMember.errors.reactivateMember', undefined, 'Failed to reactivate member'));
    } finally {
      setConfirmReactivateMember(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner text={t('settingsMember.loading')} />;
  }

  return (
    <div className="space-y-6" data-test-id="member-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('settingsMember.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('settingsMember.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            data-test-id="member-invite-button"
            onClick={() => {
              resetInviteForm();
              setIsInviteOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {t('settingsMember.actions.inviteMember')}
          </button>
        )}
      </div>

      {!isInviteOpen && (
        <MemberFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          roleFilter={roleFilter}
          onRoleChange={setRoleFilter}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          assignableRoles={assignableRoles}
        />
      )}

      {isInviteOpen && (
        <MemberInviteForm
          name={inviteName}
          email={inviteEmail}
          role={inviteRole}
          emailError={inviteEmailError}
          assignableRoles={assignableRoles}
          onNameChange={setInviteName}
          onEmailChange={(value) => {
            setInviteEmail(value);
            if (inviteEmailError) setInviteEmailError(null);
          }}
          onRoleChange={setInviteRole}
          onSubmit={handleInvite}
          onCancel={() => {
            setIsInviteOpen(false);
            resetInviteForm();
          }}
        />
      )}

      {/* Members List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {members.length === 0 ? (
          <div className="p-8 text-center italic text-gray-500">
            {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
              ? t('settingsMember.filters.noResults')
              : t('settingsMember.loading')}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {members.map((member: Member) => (
              <MemberListItem
                key={member.id}
                member={member}
                currentUserId={currentUser?.id}
                canWrite={canWrite}
                canDelete={canDelete}
                roles={rolesRaw}
                onEditRole={handleEditRole}
                onResetPassword={setConfirmResetMember}
                onRemove={setConfirmRemoveMember}
                onReactivate={setConfirmReactivateMember}
              />
            ))}
          </ul>
        )}
      </div>

      <PasswordModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        email={modalData.email}
        password={modalData.password}
        title={modalData.title}
      />

      <ConfirmationDialog
        isOpen={!!confirmResetMember}
        onClose={() => setConfirmResetMember(null)}
        onConfirm={() => void confirmReset()}
        title={t('settingsMember.modals.resetTitle')}
        message={`${t('settingsMember.modals.resetDescriptionPrefix')} ${confirmResetMember?.email ?? ''} ${t('settingsMember.modals.resetDescriptionSuffix')}`}
        confirmLabel={t('settingsMember.actions.resetPassword')}
      />

      <ConfirmationDialog
        isOpen={!!confirmRemoveMember}
        onClose={() => setConfirmRemoveMember(null)}
        onConfirm={() => void confirmRemove()}
        title={t('settingsMember.modals.removeTitle')}
        message={`${t('settingsMember.modals.removeDescriptionPrefix')} ${confirmRemoveMember?.full_name ?? ''} ${t('settingsMember.modals.removeDescriptionSuffix')} ${t('settingsMember.modals.removeWarning', undefined, 'This will deactivate the account. They will no longer be able to login, but you can reactivate them later.')}`}
        confirmLabel={t('settingsMember.actions.deactivateMember', undefined, 'Deactivate')}
        isDestructive
      />

      <ConfirmationDialog
        isOpen={!!confirmReactivateMember}
        onClose={() => setConfirmReactivateMember(null)}
        onConfirm={() => void confirmReactivate()}
        title={t('settingsMember.modals.reactivateTitle', undefined, 'Reactivate Member')}
        message={`${t('settingsMember.modals.reactivateDescription', undefined, 'Are you sure you want to reactivate account for')} ${confirmReactivateMember?.full_name ?? ''}?`}
        confirmLabel={t('settingsMember.actions.reactivateMember', undefined, 'Reactivate')}
      />

      <EditMemberRoleModal
        member={editRoleMember}
        value={editRoleValue}
        error={editRoleError}
        assignableRoles={assignableRoles}
        roles={rolesRaw}
        onChange={setEditRoleValue}
        onClose={() => {
          setEditRoleMember(null);
          setEditRoleError(null);
        }}
        onSubmit={confirmRoleUpdate}
      />

      {/* Admin role is permanent — both invite and role-update flows confirm before proceeding. */}
      <ConfirmationDialog
        isOpen={showAdminUpdateWarning}
        onClose={() => setShowAdminUpdateWarning(false)}
        onConfirm={() => void submitRoleUpdate()}
        title={t('settingsMember.modals.adminInviteWarningTitle')}
        message={t('settingsMember.modals.adminInviteWarningBody')}
        confirmLabel={t('settingsMember.actions.continue')}
        cancelLabel={t('settingsMember.actions.back')}
      />

      <ConfirmationDialog
        isOpen={showAdminInviteWarning}
        onClose={() => setShowAdminInviteWarning(false)}
        onConfirm={() => void submitInvite()}
        title={t('settingsMember.modals.adminInviteWarningTitle')}
        message={t('settingsMember.modals.adminInviteWarningBody')}
        confirmLabel={t('settingsMember.actions.continue')}
        cancelLabel={t('settingsMember.actions.back')}
      />

      <InviteErrorModal
        error={inviteError}
        roles={rolesRaw}
        onClose={() => setInviteError(null)}
        onReactivate={(member) => {
          setInviteError(null);
          setConfirmReactivateMember(member);
        }}
      />
    </div>
  );
};
